import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarDays, ChevronLeft, ChevronRight, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

// First Sunday service starts May 24 2026, then every Sunday after
const FIRST_SERVICE_DATE = new Date("2026-05-24");

export default function EventsCalendar() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dbEvents, setDbEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setDbEvents(r.data)).catch(() => {});
  }, []);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
    setSelectedDate(null);
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dateObj = new Date(currentYear, currentMonth, day);
    const events = [];

    // DB events for this date
    dbEvents.forEach((ev) => { if (ev.date === dateStr) events.push(ev); });

    // Sunday services from May 24 2026 onwards
    if (dateObj.getDay() === 0 && dateObj >= FIRST_SERVICE_DATE) {
      const isInauguration = dateStr === "2026-05-24";
      if (!isInauguration) {
        events.push({
          title: "Sunday Service",
          time: "11:00 AM - 1:00 PM",
          location: "Walton Community Hall",
          description: "Weekly worship service",
          special: false,
        });
      }
    }
    return events;
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <section id="events" data-testid="events-section" className="py-24 px-6 bg-white relative overflow-hidden">
      <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-navy/3 blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-crimson/10 text-crimson text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <CalendarDays className="w-3.5 h-3.5" /> Events
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-4">Upcoming Events</h2>
          <p className="font-body text-slate-600 text-base max-w-lg mx-auto">Stay up to date with all our services and special events.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
          <motion.div className="lg:col-span-3 bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} data-testid="calendar-widget">
            <div className="flex items-center justify-between mb-6">
              <Button variant="ghost" size="icon" onClick={prevMonth} className="rounded-full" data-testid="cal-prev"><ChevronLeft className="w-5 h-5 text-navy" /></Button>
              <h3 className="font-heading font-bold text-lg text-navy">{MONTHS[currentMonth]} {currentYear}</h3>
              <Button variant="ghost" size="icon" onClick={nextMonth} className="rounded-full" data-testid="cal-next"><ChevronRight className="w-5 h-5 text-navy" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((d) => (<div key={d} className="text-center font-body text-xs text-slate-400 font-bold py-2">{d}</div>))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => (<div key={`e-${i}`} />))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const events = getEventsForDate(day);
                const hasEvents = events.length > 0;
                const hasSpecial = events.some((e) => e.special);
                const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
                const isSelected = day === selectedDate;
                const dateObj = new Date(currentYear, currentMonth, day);
                const isSunday = dateObj.getDay() === 0 && dateObj >= FIRST_SERVICE_DATE;
                return (
                  <button key={day} onClick={() => setSelectedDate(day)}
                    className={`relative w-full aspect-square rounded-xl flex items-center justify-center font-body text-sm transition-all duration-200 ${
                      isSelected ? "bg-navy text-white font-bold"
                      : isToday ? "bg-cgreen/10 text-cgreen font-bold ring-2 ring-cgreen/30"
                      : hasSpecial ? "bg-crimson/10 text-crimson font-semibold"
                      : isSunday ? "bg-navy/5 text-navy font-semibold"
                      : "text-slate-600 hover:bg-slate-50"
                    }`} data-testid={`cal-day-${day}`}>
                    {day}
                    {hasEvents && !isSelected && <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${hasSpecial ? "bg-crimson" : "bg-navy/40"}`} />}
                  </button>
                );
              })}
            </div>
          </motion.div>

          <motion.div className="lg:col-span-2" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            {selectedDate && selectedEvents.length > 0 ? (
              <div className="space-y-4" data-testid="event-details">
                <p className="font-heading font-bold text-navy text-sm">{selectedDate} {MONTHS[currentMonth]} {currentYear}</p>
                {selectedEvents.map((ev, i) => (
                  <div key={i} className={`rounded-2xl p-5 border shadow-sm ${ev.special ? "bg-crimson/5 border-crimson/15" : "bg-white border-slate-100"}`}>
                    {ev.image && <img src={ev.image.startsWith("http") ? ev.image : (process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}${ev.image}` : ev.image)} alt={ev.title} className="w-full rounded-xl mb-4" />}
                    <h4 className="font-heading font-bold text-navy text-base mb-2">{ev.title}</h4>
                    <div className="flex items-center gap-2 text-slate-700 text-xs font-body mb-1"><Clock className="w-3.5 h-3.5" /> {ev.time}</div>
                    {ev.location && <div className="flex items-center gap-2 text-slate-700 text-xs font-body mb-2"><MapPin className="w-3.5 h-3.5" /> {ev.location}</div>}
                    <p className="font-body text-slate-700 text-sm">{ev.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-100 rounded-2xl p-8 text-center" data-testid="no-event-selected">
                <CalendarDays className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="font-body text-slate-600 text-sm">{selectedDate ? "No events on this day" : "Select a date to view events"}</p>
                <p className="font-body text-slate-500 text-xs mt-2">Sundays from 24th May are highlighted</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
